"use client";

import React, { useState } from "react";
import { toast } from "@/lib/notify";
import { FaAsterisk } from "react-icons/fa6";
import { PiFileArrowUp } from "react-icons/pi";
import Image from "next/image";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    subject: "",
    message: "",
    file: null as File | null,
  });

  const [isSending, setIsSending] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFormData({ ...formData, file: e.target.files[0] });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success("Message sent successfully");
        setFormData({
          fullName: "",
          email: "",
          subject: "",
          message: "",
          file: null,
        });
      } else {
        toast.error("Failed to send message. Please try again.", { duration: 5000 });
      }
    } catch {
      toast.error("Failed to send message. Please try again.", { duration: 5000 });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section id="contactForm" className="bg-[#27335d]">
      <div className="rounded-b-semifull bg-[#27335d]">
        <div className="rounded-b-semifull relative bg-[#27335d] py-10 text-white md:py-16">
          <div className="mx-auto mt-10 h-fit w-full max-w-4xl px-5 md:px-3 lg:px-0">
            {/* Title and Image */}
            <div className="relative flex items-center rounded-t-3xl border-[3.5px] border-b-[1.5px] border-white bg-[#27335D] py-7 text-center text-sm font-semibold text-white xsm:text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl">
              <h3 className="flex-1 text-center">Contact us!</h3>
              <div className="absolute">
                <Image
                  alt="Rawiah"
                  src="/images/RawiahLogos/headerlogo.svg"
                  width={100}
                  height={100}
                  unoptimized
                  priority={true}
                  loading="eager"
                  className="w-20 ps-5 xsm:w-20 xsm:ps-5 sm:w-24 sm:ps-5 md:w-36 md:ps-10 lg:w-40 xl:w-44"
                />
              </div>
            </div>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              data-testid="contactFormForm"
              className="rounded-3xl rounded-t-none border-[1.5px] border-white bg-white px-10 py-10 xsm:px-10 sm:px-12 md:px-14 lg:px-16 xl:px-20"
            >
              {/* Full Name and Email */}
              <div className="flex flex-col justify-between md:flex-row md:gap-6">
                {/* Full Name Input */}
                <div className="mb-6 flex w-full flex-col items-start justify-start md:w-1/2">
                  <div className="flex items-center gap-1">
                    <label
                      htmlFor="fullName"
                      className="ml-1 font-semibold text-[#27335d]"
                    >
                      Full Name
                    </label>
                    <FaAsterisk className="text-sm text-[#D76B67]" />
                  </div>
                  <div className="mt-2 w-full">
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Full Name"
                      required
                      data-testid="contactFormNameInput"
                      className="w-full rounded-xl border-[3.5px] border-[#27335d] bg-white px-4 py-2 text-start font-medium text-[#27335D] placeholder:text-sm focus:border-[#27335d] focus:outline-none"
                    />
                  </div>
                </div>

                {/* Email Input */}
                <div className="mb-6 flex w-full flex-col items-start justify-start md:w-1/2">
                  <div className="flex items-center gap-1">
                    <label
                      htmlFor="email"
                      className="ml-1 font-semibold text-[#27335d]"
                    >
                      Email
                    </label>
                    <FaAsterisk className="text-sm text-[#D76B67]" />
                  </div>
                  <div className="mt-2 w-full">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="user@mail.com"
                      required
                      data-testid="contactFormEmailInput"
                      className="w-full rounded-xl border-[3.5px] border-[#27335d] bg-white px-4 py-2 text-start font-medium text-[#27335D] placeholder:text-sm focus:border-[#27335d] focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Subject Input (Full Width) */}
              <div className="mb-6 flex w-full flex-col items-start justify-start">
                <div className="flex items-center gap-1">
                  <label
                    htmlFor="subject"
                    className="ml-1 font-semibold text-[#27335d]"
                  >
                    Subject
                  </label>
                  <FaAsterisk className="text-sm text-[#D76B67]" />
                </div>
                <div className="mt-2 w-full">
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Subject"
                    required
                    data-testid="contactFormSubjectInput"
                    className="w-full rounded-xl border-[3.5px] border-[#27335d] bg-white px-4 py-2 text-start font-medium text-[#27335D] placeholder:text-sm focus:border-[#27335d] focus:outline-none"
                  />
                </div>
              </div>

              {/* Message Input */}
              <div className="mb-6 flex w-full flex-col items-start justify-start">
                <div className="flex items-center gap-1">
                  <label
                    htmlFor="message"
                    className="ml-1 font-semibold text-[#27335d]"
                  >
                    Message
                  </label>
                  <FaAsterisk className="text-sm text-[#D76B67]" />
                </div>
                <div className="mt-2 w-full">
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={5}
                    placeholder="Write your Message"
                    required
                    data-testid="contactFormMessageTextarea"
                    className="flex w-full resize-none rounded-xl border-[3.5px] border-[#27335d] bg-white px-4 py-2 text-start font-medium text-[#27335D] placeholder:text-sm focus:border-[#27335d] focus:outline-none"
                  />
                  {/* Character Counter */}
                  <div className="mt-1.5 text-center text-xs font-semibold text-[#7C85A1] md:mr-1.5 md:text-end">
                    {500 - formData.message.length} characters remaining
                  </div>
                </div>
              </div>

              {/* File Input */}
              {/* <div className="flex flex-col items-start justify-start">
                <label htmlFor="file" className="font-semibold text-[#27335d]">
                  Upload File (Optional)
                </label>
                <div className="mt-2 flex items-center gap-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-4 flex items-center">
                      <PiFileArrowUp className="text-2xl text-[#27335d]" />
                    </div>
                    <input
                      id="file"
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const fileLabel = document.getElementById("fileLabel");
                        const removeText =
                          document.getElementById("removeText");
                        if (fileLabel && removeText && e.target.files?.[0]) {
                          fileLabel.textContent = `Selected: ${e.target.files[0].name}`;
                          removeText.style.display = "inline"; // Show 'Remove' text
                        }
                      }}
                    />
                    <label
                      id="fileLabel"
                      htmlFor="file"
                      className="flex cursor-pointer items-center rounded-full border-[3.5px] border-[#27335d] bg-white px-12 py-2 text-start font-semibold text-[#27335D] placeholder:text-sm focus:border-[#27335d] focus:outline-none"
                    >
                      Choose File
                    </label>
                  </div>
                  <button
                    type="button"
                    id="removeText"
                    onClick={() => {
                      const fileInput = document.getElementById(
                        "file",
                      ) as HTMLInputElement;
                      const fileLabel = document.getElementById("fileLabel");
                      const removeText = document.getElementById("removeText");

                      if (fileInput) fileInput.value = ""; // Reset file input
                      if (fileLabel) fileLabel.textContent = "Choose File"; // Reset label
                      if (removeText) removeText.style.display = "none"; // Hide 'Remove' text
                    }}
                    className="hidden cursor-pointer text-sm font-semibold text-[#D76B67] hover:underline"
                  >
                    Remove
                  </button>
                </div>
                <p className="mt-2 text-xs font-semibold text-[#7C85A1]">
                  Maximum file size: 5MB
                </p>
              </div> */}

              {/* Send Button */}
              <div className="mt-5 flex flex-col items-center justify-center">
                <button
                  type="submit"
                  disabled={isSending}
                  data-testid="contactFormSubmitButton"
                  className="cursor-pointer rounded-full border-b-[5.5px] border-[#4d4d4d] bg-[#27335D] font-bold text-white transition duration-150 ease-in-out hover:bg-[#525c7d] active:scale-95 xsm:px-10 xsm:py-1.5 xsm:text-sm sm:px-12 sm:py-1.5 sm:text-base md:px-14 md:py-2 md:text-lg lg:px-16 lg:py-2.5 lg:text-xl xl:px-20 xl:py-3 xl:text-2xl"
                >
                  {isSending ? "Sending..." : "Send"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
